import type { DetalheDaConta, MetasDaConta } from "@base/contracts";
import { Botao, CampoDeSelecao, CampoDeTexto, EstadoVazio, Etiqueta, cn } from "@base/ui";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { formatar } from "../../dashboard";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { Link, usarParametro } from "../../../shared/navegacao/navegacao";
import { CAMINHO_DAS_CONEXOES_DA_MONTE } from "../../conexoes/presentation/acesso-da-monte";
import { useCorrigirTipo, useGerarLeituraDaIa, useLeituraDaIa, useSalvarMetas } from "../application/central.contexto";
import { BarrasDaPiora } from "./barras-da-piora";
import { FunilDaSafra } from "./funil-da-safra";
import { GraficoDaSerie } from "./grafico-da-serie";
import { LinhaDoTempo } from "./linha-do-tempo";
import { SeloDaSituacao } from "./selo-da-situacao";
import {
  ROTULO_DA_PLATAFORMA,
  frase,
  leituraDaVariacao,
  moedaDeCentavos,
  rotuloDaEtapa,
  rotuloDeNumero,
  rotuloDeQuemAge,
  rotuloDoSinal,
  tomDoSinal,
  type SentidoDaVariacao,
} from "./textos";

const CARTAO = "min-w-0 rounded-xl border border-border bg-card";
const CABECA = "border-b border-border px-3 py-2.5 text-right text-xs font-semibold text-muted-foreground";
const CELULA = "border-b border-border/60 p-3 text-right tabular-nums";

const CLASSE_DA_VARIACAO: Readonly<Record<SentidoDaVariacao, string>> = {
  pior: "bg-destructive/12 text-destructive",
  melhor: "bg-success/12 text-success",
  neutro: "bg-muted text-foreground",
};

export const CAMINHO_DA_CONTA = "/gestao/central/conta";

export function caminhoDaConta(cliente: string, campanha: string, tipo = ""): string {
  const busca = new URLSearchParams({ cliente, campanha });
  if (tipo) busca.set("tipo", tipo);
  return `${CAMINHO_DA_CONTA}?${busca.toString()}`;
}

/** Detalhe da conta. Sem objeto, mostra o cliente e a campanha que vieram na URL. */
export function PaginaDaConta({ detalhe }: { readonly detalhe: DetalheDaConta | null }) {
  if (!detalhe) return <ContaSemDetalhe />;
  return <ContaComDetalhe detalhe={detalhe} />;
}

function Caminho({ atual }: { readonly atual: string }) {
  return (
    <nav aria-label="Caminho" className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      <Link
        para="/gestao/central"
        className="inline-flex min-h-11 items-center font-semibold text-primary underline-offset-4 hover:underline"
      >
        Central
      </Link>
      <span aria-hidden="true">/</span>
      <span>{atual}</span>
    </nav>
  );
}

function ContaSemDetalhe() {
  const cliente = usarParametro("cliente");
  const campanha = usarParametro("campanha");
  return (
    <div className="flex min-w-0 flex-col gap-6">
      <Caminho atual={cliente ?? "Conta"} />
      <div>
        <h1 className="text-3xl font-bold leading-tight text-foreground">{cliente ?? "Conta"}</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          {campanha ? `Campanha ${campanha}.` : "Abra uma linha da fila para ver a conta."}
        </p>
      </div>
      <EstadoVazio
        titulo="Sem detalhe desta conta"
        descricao="A visão geral ainda não traz o diagnóstico completo."
      />
    </div>
  );
}

function ContaComDetalhe({ detalhe }: { readonly detalhe: DetalheDaConta }) {
  const clienteAberto = usarParametro("cliente") ?? detalhe.cliente;
  const campanhaAberta = usarParametro("campanha");
  const tipoAberto = usarParametro("tipo");
  const plataforma = ROTULO_DA_PLATAFORMA[detalhe.plataforma];
  return (
    <div className="flex min-w-0 flex-col gap-6">
      <Caminho atual={detalhe.cliente} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <h1 className="text-3xl font-bold leading-tight text-foreground">{detalhe.cliente}</h1>
          <SeloDaSituacao situacao={detalhe.situacao} grande />
          <span className="text-sm text-muted-foreground">
            {plataforma} · {formatar.quantidade(detalhe.campanhas.length)}{" "}
            {detalhe.campanhas.length === 1 ? "tipo" : "tipos"}
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-sm text-muted-foreground">Em jogo</span>
          <span className="text-[26px] font-bold tabular-nums text-foreground">{moedaDeCentavos(detalhe.emJogoCentavos)}</span>
          <span className="text-sm text-muted-foreground">/semana</span>
        </div>
      </div>

      {detalhe.campanhas.length > 0 ? (
        <div role="group" aria-label="Tipos da conta" className="flex flex-wrap gap-2">
          {detalhe.campanhas.map((aba, indice) => {
            const destino = aba.campanha ?? aba.rotulo;
            const aberta = tipoAberto
              ? aba.tipoCodigo === tipoAberto
              : campanhaAberta
                ? destino === campanhaAberta
                : indice === 0;
            return (
              <Link
                key={`${aba.rotulo}|${destino}|${aba.tipoCodigo ?? ""}`}
                para={caminhoDaConta(clienteAberto, destino, aba.tipoCodigo ?? "")}
                aria-current={aberta ? "page" : undefined}
                className={cn(
                  "inline-flex min-h-11 items-center rounded-full border px-4 text-sm",
                  aberta
                    ? "border-foreground bg-foreground font-semibold text-background"
                    : "border-input bg-card text-foreground",
                )}
              >
                {aba.rotulo}
              </Link>
            );
          })}
          <a
            href="#titulo-safra"
            className="inline-flex min-h-11 items-center rounded-full border border-input bg-card px-4 text-sm text-foreground"
          >
            Negócio · Kommo
          </a>
        </div>
      ) : null}

      <div className="flex flex-wrap items-start gap-6">
        <div className="flex min-w-0 flex-[999_1_600px] flex-col gap-6">
          <MetricaPrincipal
            detalhe={detalhe}
            titulo={
              detalhe.campanhas.find((aba, indice) => {
                const destino = aba.campanha ?? aba.rotulo;
                return tipoAberto
                  ? aba.tipoCodigo === tipoAberto
                  : campanhaAberta
                    ? destino === campanhaAberta
                    : indice === 0;
              })?.rotulo ?? "atual da semana"
            }
          />

          <Cartao id="titulo-piora" titulo="De onde veio a piora" descricao="As secundárias não geram alerta; elas explicam a principal.">
            <BarrasDaPiora etapas={detalhe.piora} />
            <dl className="flex flex-wrap gap-x-8 gap-y-3 rounded-lg bg-muted p-4">
              <Diagnostico termo="Causa provável" valor={frase(detalhe.causa)} forte />
              <Diagnostico termo="Ação típica" valor={frase(detalhe.acaoTipica)} />
              <Diagnostico termo="Quem age" valor={rotuloDeQuemAge(detalhe.quemAge)} />
            </dl>
          </Cartao>

          <Cartao
            id="titulo-anuncios"
            titulo="Anúncios da campanha · últimos 7 dias"
            descricao="Agendamentos atribuídos pela UTM do anúncio no Kommo."
          >
            <Anuncios detalhe={detalhe} />
          </Cartao>

          {detalhe.palavrasChave && detalhe.palavrasChave.length > 0 ? (
            <Cartao
              id="titulo-palavras"
              titulo="Índice de qualidade · palavras-chave"
              descricao="Retrato diário da campanha de pesquisa. A nota é de 1 a 10."
            >
              <PalavrasChave palavras={detalhe.palavrasChave} />
            </Cartao>
          ) : null}
        </div>

        <aside className="flex min-w-0 flex-[1_1_360px] flex-col gap-6">
          <LeituraDaIa detalhe={detalhe} />

          <Cartao id="titulo-alteracoes" titulo="Alterações na conta" lateral>
            <LinhaDoTempo alteracoes={detalhe.alteracoes} />
          </Cartao>

          <Cartao id="titulo-safra" titulo="Negócio · safra" descricao="Leads que chegaram no mês, acompanhados no Kommo." lateral>
            <FunilDaSafra etapas={detalhe.safra} />
            <dl className="grid grid-cols-2 gap-3 pt-1">
              <Quadro termo="Custo por agendamento" valor={moedaDeCentavos(detalhe.custoPorAgendamentoCentavos)} />
              <Quadro termo="Custo por paciente" valor={moedaDeCentavos(detalhe.custoPorPacienteCentavos)} />
              <Quadro termo="ROAS" valor={formatar.multiplicador(detalhe.roas)} />
            </dl>
            <p className="text-xs text-muted-foreground">
              Percentuais são de uma etapa para a seguinte. Investimento: {moedaDeCentavos(detalhe.investimentoSafraCentavos)}.
            </p>
            <ComparativoDoNegocio periodos={detalhe.periodosDoNegocio} />
          </Cartao>

          <SecaoMetas detalhe={detalhe} />
        </aside>
      </div>
    </div>
  );
}

function nomeDaCampanha(detalhe: DetalheDaConta, aberta: string | null): string {
  const aba =
    detalhe.campanhas.find((item) => (item.campanha ?? item.rotulo) === aberta) ?? detalhe.campanhas[0];
  return aba?.campanha ?? aba?.rotulo ?? aberta ?? "";
}

function LeituraDaIa({ detalhe }: { readonly detalhe: DetalheDaConta }) {
  const campanha = nomeDaCampanha(detalhe, usarParametro("campanha"));
  const guardada = useLeituraDaIa({ clienteId: detalhe.clienteId, plataforma: detalhe.plataforma, campanha });
  const gerar = useGerarLeituraDaIa();
  const leitura = guardada.data?.leitura ?? null;
  const semChave = gerar.error instanceof ErroDaApi && gerar.error.problema?.type.endsWith("/chave-de-ia-ausente");
  const pediu = useRef(false);

  useEffect(() => {
    pediu.current = false;
  }, [detalhe.clienteId, detalhe.plataforma, campanha]);

  useEffect(() => {
    if (pediu.current || !campanha || !guardada.isSuccess || leitura || gerar.isPending || gerar.isError) return;
    pediu.current = true;
    gerar.mutate({ cliente: detalhe.cliente, campanha });
  }, [campanha, detalhe.cliente, gerar, guardada.isSuccess, leitura]);

  return (
    <Cartao id="titulo-leitura-ia" titulo="Leitura da IA" lateral>
      {leitura ? (
        <>
          <p className="text-sm leading-relaxed text-foreground">{leitura.resumo}</p>
          {leitura.acoes.length > 0 ? (
            <ol aria-label="Ações sugeridas" className="flex list-decimal flex-col gap-2 pl-5 text-sm text-foreground">
              {leitura.acoes.map((acao) => (
                <li key={acao}>{acao}</li>
              ))}
            </ol>
          ) : null}
          <p className="text-xs text-muted-foreground">
            {leitura.modelo} · {formatar.momento(leitura.geradaEm)}
          </p>
        </>
      ) : gerar.isPending || guardada.isPending ? (
        <p className="text-sm text-muted-foreground">Gerando a leitura da semana…</p>
      ) : (
        <p className="text-sm text-muted-foreground">Nenhuma leitura desta campanha ainda.</p>
      )}
      {gerar.isError ? (
        <p role="alert" className="text-sm font-medium text-destructive">
          {mensagemDoErro(gerar.error)}{" "}
          {semChave ? (
            <Link para={CAMINHO_DAS_CONEXOES_DA_MONTE} className="font-semibold text-primary underline underline-offset-4">
              Abrir Conexões
            </Link>
          ) : null}
        </p>
      ) : null}
      <p className="border-t border-border pt-3 text-xs text-muted-foreground">
        A IA só sugere. Nada é alterado na conta sem o gestor.
      </p>
    </Cartao>
  );
}

function MetricaPrincipal({ detalhe, titulo }: { readonly detalhe: DetalheDaConta; readonly titulo: string }) {
  const meta = leituraDaVariacao(detalhe.variacaoMetaRotulo);
  const base = leituraDaVariacao(detalhe.variacaoBaseRotulo);
  return (
    <section aria-labelledby="titulo-principal" className={cn(CARTAO, "flex flex-col gap-[18px] p-6")}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 id="titulo-principal" className="text-sm font-semibold text-muted-foreground">
            Métrica principal · {titulo}
          </h2>
          <span className="text-[44px] font-bold leading-[1.05] tabular-nums text-foreground">{detalhe.atualRotulo}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className={cn("rounded-lg px-3 py-2 text-sm font-semibold", CLASSE_DA_VARIACAO[meta.sentido])}>
            {meta.sentido === "neutro" ? `Meta de ${detalhe.metaRotulo}` : `${detalhe.variacaoMetaRotulo} vs meta de ${detalhe.metaRotulo}`}
          </span>
          <span className={cn("rounded-lg px-3 py-2 text-sm font-semibold", CLASSE_DA_VARIACAO.neutro)}>
            {base.sentido === "neutro" ? `Base de ${detalhe.baseRotulo}` : `${detalhe.variacaoBaseRotulo} vs base de ${detalhe.baseRotulo}`}
          </span>
        </div>
      </div>
      <GraficoDaSerie
        serie={detalhe.serie}
        rotulo="Série semanal"
        ultimoEmAlerta={meta.sentido === "pior"}
        meta={detalhe.metaValor}
        base={detalhe.baseValor}
      />
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-muted-foreground">
        <span>{detalhe.volumeRotulo} na semana</span>
        <span>Gasto da semana: {moedaDeCentavos(detalhe.gastoCentavos)}</span>
        {detalhe.indiceQualidade != null ? <span>Índice de qualidade: {rotuloDeNumero(detalhe.indiceQualidade)}</span> : null}
        <span>Base = mediana das 4 semanas anteriores</span>
      </div>
    </section>
  );
}

function Anuncios({ detalhe }: { readonly detalhe: DetalheDaConta }) {
  if (detalhe.anuncios.length === 0) return <EstadoVazio titulo="Nenhum anúncio nesta conta." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">Anúncios</caption>
        <thead>
          <tr>
            <th scope="col" className={cn(CABECA, "pl-0 text-left")}>Anúncio</th>
            <th scope="col" className={CABECA}>Gasto</th>
            <th scope="col" className={CABECA}>CTR</th>
            <th scope="col" className={CABECA}>CPL</th>
            <th scope="col" className={CABECA}>Agendamentos</th>
            <th scope="col" className={cn(CABECA, "pr-0 text-left")}>Sinal</th>
          </tr>
        </thead>
        <tbody>
          {detalhe.anuncios.map((anuncio) => (
            <tr key={`${anuncio.nome}|${anuncio.gastoCentavos}|${anuncio.cplRotulo}`} className="[&:last-child>*]:border-b-0">
              <th scope="row" className={cn(CELULA, "pl-0 text-left font-semibold")}>
                <span className="break-words">{anuncio.nome}</span>
              </th>
              <td className={CELULA}>{moedaDeCentavos(anuncio.gastoCentavos)}</td>
              <td className={CELULA}>{anuncio.ctrRotulo}</td>
              <td className={cn(CELULA, "font-bold")}>{anuncio.cplRotulo}</td>
              <td className={CELULA}>{formatar.quantidade(anuncio.agendamentos)}</td>
              <td className={cn(CELULA, "pr-0 text-left")}>
                <Etiqueta tom={tomDoSinal(anuncio.sinal)} className="whitespace-nowrap py-1 font-semibold">
                  {rotuloDoSinal(anuncio.sinal)}
                </Etiqueta>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PalavrasChave({
  palavras,
}: {
  readonly palavras: NonNullable<DetalheDaConta["palavrasChave"]>;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">Palavras-chave</caption>
        <thead>
          <tr>
            <th scope="col" className={cn(CABECA, "pl-0 text-left")}>
              Palavra
            </th>
            <th scope="col" className={CABECA}>
              Índice
            </th>
            <th scope="col" className={cn(CABECA, "pr-0")}>
              Impressões
            </th>
          </tr>
        </thead>
        <tbody>
          {palavras.map((palavra) => (
            <tr key={palavra.texto} className="[&:last-child>*]:border-b-0">
              <th scope="row" className={cn(CELULA, "max-w-[18rem] pl-0 text-left font-semibold")}>
                <span className="break-words">{palavra.texto}</span>
              </th>
              <td className={CELULA}>{palavra.indiceQualidade == null ? "—" : rotuloDeNumero(palavra.indiceQualidade)}</td>
              <td className={cn(CELULA, "pr-0")}>{formatar.quantidade(palavra.impressoes)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ComparativoDoNegocio({ periodos }: { readonly periodos: DetalheDaConta["periodosDoNegocio"] }) {
  if (periodos.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">Comparativo mensal do negócio</caption>
        <thead>
          <tr>
            <th scope="col" className={cn(CABECA, "pl-0 text-left")}>Período</th>
            <th scope="col" className={CABECA}>Custo/agend.</th>
            <th scope="col" className={CABECA}>Custo/paciente</th>
            <th scope="col" className={cn(CABECA, "pr-0")}>ROAS</th>
          </tr>
        </thead>
        <tbody>
          {periodos.map((periodo) => (
            <tr key={periodo.rotulo} className="[&:last-child>*]:border-b-0">
              <th scope="row" className={cn(CELULA, "pl-0 text-left font-semibold")}>
                {periodo.rotulo}
              </th>
              <td className={CELULA}>{moedaDeCentavos(periodo.custoPorAgendamentoCentavos)}</td>
              <td className={CELULA}>{moedaDeCentavos(periodo.custoPorPacienteCentavos)}</td>
              <td className={cn(CELULA, "pr-0")}>{formatar.multiplicador(periodo.roas)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Diagnostico({ termo, valor, forte = false }: { readonly termo: string; readonly valor: string; readonly forte?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{termo}</dt>
      <dd className={cn("break-words text-[15px] text-foreground", forte ? "font-bold" : "font-semibold")}>{valor}</dd>
    </div>
  );
}

function Quadro({ termo, valor }: { readonly termo: string; readonly valor: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-lg bg-muted p-3">
      <dt className="text-xs text-muted-foreground">{termo}</dt>
      <dd className="text-xl font-bold tabular-nums text-foreground">{valor}</dd>
    </div>
  );
}

function Cartao({
  id,
  titulo,
  descricao,
  acao,
  lateral = false,
  children,
}: {
  readonly id: string;
  readonly titulo: string;
  readonly descricao?: string;
  readonly acao?: ReactNode;
  readonly lateral?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className={cn(CARTAO, "flex flex-col gap-4", lateral ? "p-[22px]" : "p-6")}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 id={id} className="text-lg font-bold text-foreground">
            {titulo}
          </h2>
          {descricao ? <p className="mt-1 text-[13px] text-muted-foreground">{descricao}</p> : null}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

type ChaveDaMeta = keyof MetasDaConta;
type Rascunho = Record<ChaveDaMeta, string>;
type ErrosDoRascunho = Record<ChaveDaMeta, readonly string[]>;

const ERRO_INTEIRO = "Informe um número inteiro, zero ou maior.";
const ERRO_NUMERO = "Informe um número, zero ou maior.";

const ERROS_VAZIOS: ErrosDoRascunho = {
  cplCentavos: [],
  tetoCentavos: [],
  cpmMaximoCentavos: [],
  ctrMinimo: [],
  hookRateMinimo: [],
  frequenciaMaxima: [],
  saldoMinimoDias: [],
};

const CAMPOS: readonly {
  readonly chave: ChaveDaMeta;
  readonly rotulo: string;
  readonly dica: string;
  readonly inteiro: boolean;
}[] = [
  { chave: "cplCentavos", rotulo: "CPL", dica: "Unidade: R$ (centavos)", inteiro: true },
  { chave: "tetoCentavos", rotulo: "Teto", dica: "Unidade: R$ (centavos)", inteiro: true },
  { chave: "cpmMaximoCentavos", rotulo: "CPM máximo", dica: "Unidade: R$ (centavos)", inteiro: true },
  { chave: "ctrMinimo", rotulo: "CTR mínimo", dica: "Unidade: %", inteiro: false },
  { chave: "hookRateMinimo", rotulo: "Hook rate mínimo (%)", dica: "Unidade: %", inteiro: false },
  { chave: "frequenciaMaxima", rotulo: "Frequência máxima", dica: "Unidade: vezes", inteiro: false },
  { chave: "saldoMinimoDias", rotulo: "Saldo mínimo", dica: "Unidade: dias", inteiro: true },
];

function rascunhoInicial(metas: MetasDaConta): Rascunho {
  return {
    cplCentavos: String(metas.cplCentavos),
    tetoCentavos: String(metas.tetoCentavos),
    cpmMaximoCentavos: String(metas.cpmMaximoCentavos),
    ctrMinimo: String(metas.ctrMinimo),
    hookRateMinimo: String(metas.hookRateMinimo),
    frequenciaMaxima: String(metas.frequenciaMaxima),
    saldoMinimoDias: String(metas.saldoMinimoDias),
  };
}

function inteiroNaoNegativo(valor: string): number | null {
  const texto = valor.trim();
  if (!/^\d+$/.test(texto)) return null;
  return Number(texto);
}

function numeroNaoNegativo(valor: string): number | null {
  const texto = valor.trim().replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(texto)) return null;
  return Number(texto);
}

function lerRascunho(
  rascunho: Rascunho,
): { readonly ok: true; readonly metas: MetasDaConta } | { readonly ok: false; readonly erros: ErrosDoRascunho } {
  const cplCentavos = inteiroNaoNegativo(rascunho.cplCentavos);
  const tetoCentavos = inteiroNaoNegativo(rascunho.tetoCentavos);
  const cpmMaximoCentavos = inteiroNaoNegativo(rascunho.cpmMaximoCentavos);
  const ctrMinimo = numeroNaoNegativo(rascunho.ctrMinimo);
  const hookRateMinimo = numeroNaoNegativo(rascunho.hookRateMinimo);
  const frequenciaMaxima = numeroNaoNegativo(rascunho.frequenciaMaxima);
  const saldoMinimoDias = inteiroNaoNegativo(rascunho.saldoMinimoDias);
  const erros: ErrosDoRascunho = {
    cplCentavos: cplCentavos === null ? [ERRO_INTEIRO] : [],
    tetoCentavos: tetoCentavos === null ? [ERRO_INTEIRO] : [],
    cpmMaximoCentavos: cpmMaximoCentavos === null ? [ERRO_INTEIRO] : [],
    ctrMinimo: ctrMinimo === null ? [ERRO_NUMERO] : [],
    hookRateMinimo: hookRateMinimo === null ? [ERRO_NUMERO] : [],
    frequenciaMaxima: frequenciaMaxima === null ? [ERRO_NUMERO] : [],
    saldoMinimoDias: saldoMinimoDias === null ? [ERRO_INTEIRO] : [],
  };
  if (
    cplCentavos === null ||
    tetoCentavos === null ||
    cpmMaximoCentavos === null ||
    ctrMinimo === null ||
    hookRateMinimo === null ||
    frequenciaMaxima === null ||
    saldoMinimoDias === null
  ) {
    return { ok: false, erros };
  }
  return {
    ok: true,
    metas: {
      cplCentavos,
      tetoCentavos,
      cpmMaximoCentavos,
      ctrMinimo,
      hookRateMinimo,
      frequenciaMaxima,
      saldoMinimoDias,
    },
  };
}

function SecaoMetas({ detalhe }: { readonly detalhe: DetalheDaConta }) {
  const salvar = useSalvarMetas();
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState<Rascunho>(() => rascunhoInicial(detalhe.metas));
  const [erros, setErros] = useState<ErrosDoRascunho>(ERROS_VAZIOS);
  const [aviso, setAviso] = useState<string | null>(null);

  function comecar() {
    setRascunho(rascunhoInicial(detalhe.metas));
    setErros(ERROS_VAZIOS);
    setAviso(null);
    setEditando(true);
  }

  function cancelar() {
    setErros(ERROS_VAZIOS);
    setEditando(false);
  }

  function gravar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    const lido = lerRascunho(rascunho);
    if (!lido.ok) {
      setErros(lido.erros);
      setAviso(null);
      return;
    }
    setErros(ERROS_VAZIOS);
    setAviso(null);
    void salvar
      .mutateAsync({
        clienteId: detalhe.clienteId,
        plataforma: detalhe.plataforma,
        contaId: detalhe.contaId,
        tipoCodigo: detalhe.tipoCodigo,
        verbaDiariaCentavos: detalhe.verbaDiariaCentavos,
        ...lido.metas,
      })
      .then(() => {
        setAviso("Metas salvas.");
        setEditando(false);
      })
      .catch(() => undefined);
  }

  return (
    <Cartao
      id="titulo-metas"
      titulo="Metas da conta"
      lateral
      acao={
        editando ? null : (
          <Botao variante="contorno" className="h-11 rounded-lg px-3.5 text-[13px] font-semibold" onClick={comecar}>
            Editar metas
          </Botao>
        )
      }
    >
      {aviso ? (
        <p role="status" className="text-sm font-medium text-success">
          {aviso}
        </p>
      ) : null}
      <TipoDaCampanha detalhe={detalhe} />
      {editando ? (
        <form className="flex min-w-0 flex-col gap-texto" onSubmit={gravar}>
          {CAMPOS.map((campo) => (
            <CampoDeTexto
              key={campo.chave}
              className="min-w-0"
              rotulo={campo.rotulo}
              inputMode={campo.inteiro ? "numeric" : "decimal"}
              autoComplete="off"
              value={rascunho[campo.chave]}
              erros={erros[campo.chave]}
              dica={campo.dica}
              onChange={(evento) => setRascunho((atual) => ({ ...atual, [campo.chave]: evento.target.value }))}
            />
          ))}
          <div className="flex flex-wrap gap-texto">
            <Botao type="submit" tamanho="pequeno" className="cursor-pointer" carregando={salvar.isPending}>
              Salvar
            </Botao>
            <Botao
              type="button"
              variante="contorno"
              tamanho="pequeno"
              className="cursor-pointer"
              onClick={cancelar}
              disabled={salvar.isPending}
            >
              Cancelar
            </Botao>
          </div>
          {salvar.isError ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {mensagemDoErro(salvar.error)}
            </p>
          ) : null}
        </form>
      ) : (
        <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-2.5 text-sm">
          <Meta termo="CPL (meta)" valor={moedaDeCentavos(detalhe.metas.cplCentavos)} />
          <Meta termo="Teto econômico do CPL" valor={moedaDeCentavos(detalhe.metas.tetoCentavos)} />
          <Meta termo="CPM máximo" valor={moedaDeCentavos(detalhe.metas.cpmMaximoCentavos)} />
          <Meta termo="CTR mínimo" valor={`${rotuloDeNumero(detalhe.metas.ctrMinimo)}%`} />
          <Meta termo="Hook rate mínimo" valor={`${rotuloDeNumero(detalhe.metas.hookRateMinimo)}%`} />
          <Meta termo="Frequência máxima" valor={rotuloDeNumero(detalhe.metas.frequenciaMaxima)} />
          <Meta termo="Saldo mínimo" valor={formatar.dias(detalhe.metas.saldoMinimoDias)} />
        </dl>
      )}
    </Cartao>
  );
}

/** O tipo vem do objetivo na plataforma; a Monte corrige aqui e a meta que casa muda junto. */
function TipoDaCampanha({ detalhe }: { readonly detalhe: DetalheDaConta }) {
  const corrigir = useCorrigirTipo();
  const campanha = nomeDaCampanha(detalhe, usarParametro("campanha"));
  if (!campanha || detalhe.tiposDisponiveis.length === 0) return null;
  const conhecido = detalhe.tiposDisponiveis.some((tipo) => tipo.codigo === detalhe.tipoCodigo);
  return (
    <div className="flex flex-col gap-1.5">
      <CampoDeSelecao
        rotulo="Corrigir tipo da campanha"
        value={conhecido ? detalhe.tipoCodigo : ""}
        textoVazio={conhecido ? undefined : "Sem tipo"}
        opcoes={detalhe.tiposDisponiveis.map((tipo) => ({ valor: tipo.codigo, rotulo: tipo.nome }))}
        disabled={corrigir.isPending}
        onChange={(evento) => {
          if (!evento.target.value) return;
          corrigir.mutate({
            campanha: { clienteId: detalhe.clienteId, plataforma: detalhe.plataforma, campanha },
            tipoCodigo: evento.target.value,
          });
        }}
      />
      {corrigir.isSuccess ? (
        <p role="status" className="text-sm font-medium text-success">
          Tipo salvo. A coleta diária não sobrescreve.
        </p>
      ) : null}
      {corrigir.isError ? (
        <p role="alert" className="text-sm font-medium text-destructive">
          {mensagemDoErro(corrigir.error)}
        </p>
      ) : null}
    </div>
  );
}

function Meta({ termo, valor }: { readonly termo: string; readonly valor: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{termo}</dt>
      <dd className="m-0 text-right font-semibold tabular-nums text-foreground">{valor}</dd>
    </>
  );
}
